import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Button, Row, Col, Container, Alert, FloatingLabel, Card } from "react-bootstrap";
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';

const isNumericOnlyToken = (value) => typeof value === 'string' && /^\d+$/.test(value);

const Stepper = ({ step, steps }) => (
  <div className="d-flex justify-content-center align-items-center mb-4 gap-md-4 gap-2" style={{ maxWidth: 500, margin: "0 auto", position: 'relative' }}>
    {steps.map((s, idx) => (
      <React.Fragment key={s.label}>
        <div className="text-center flex-fill d-flex flex-column align-items-center" style={{ position: 'relative', zIndex: 2 }}>
          <div
            className={`rounded-3 mb-3 d-flex align-items-center justify-content-center ${
              idx + 1 === step
                ? "bg-primary text-white"
                : idx + 1 < step
                ? "bg-success text-white"
                : "bg-light text-secondary"
            }`}
            style={{ width: 36, height: 36, fontWeight: "bold", fontSize: 18 }}
          >
            {idx + 1}
          </div>
          <div style={{ fontSize: 13, fontWeight: idx + 1 === step ? "bold" : "normal" }}>{s.label}</div>
        </div>
        {idx < steps.length - 1 && (
          <div
            style={{
              flex: 1,
              height: 4,
              background: idx + 1 < step ? "#198754" : "#dee2e6",
              margin: '.35rem 0.5rem',
              minWidth: 40,
              maxWidth: 80,
              alignSelf: 'end',
              borderRadius: 2,
              zIndex: 1,
            }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

const CompleteProfile = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [inviteLinkInvalid, setInviteLinkInvalid] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState({
    street: "",
    apt: "",
    city: "",
    state: "",
    zip: "",
  });
  const [isDirector, setIsDirector] = useState(false);
  const [programSettings, setProgramSettings] = useState({
    duration: "",
    maxPGYLevel: "",
    residentCount: ""
  });

  // Check profile status on component mount
  useEffect(() => {
    const checkProfileStatus = async () => {
      if (!token || isNumericOnlyToken(token)) {
        setInviteLinkInvalid(true);
        setCheckingStatus(false);
        return;
      }

      try {
        const res = await fetch(`${API_ENDPOINTS.AUTH}/profile-status/${token}`);
        if (res.ok) {
          const data = await res.json();
          setProfileCompleted(data.profileCompleted);
          setUserInfo(data);
          setIsDirector(data.role === 'director');
          if (data.program) {
            setProgramSettings({
              duration: data.program.duration ?? "",
              maxPGYLevel: data.program.maxPGYLevel ?? "",
              residentCount: data.program.residentCount ?? ""
            });
          }
          
          // Prefill name fields from existing user info if available
          if (data.name) {
            const parts = data.name.split(' ');
            if (!firstName) setFirstName(parts[0] || '');
            if (!lastName && parts.length > 1) setLastName(parts.slice(1).join(' '));
          }
          
          if (data.profileCompleted) {
            setError("This profile has already been completed. Please log in instead.");
          }
        } else {
          setInviteLinkInvalid(true);
        }
      } catch (error) {
        console.error("Error checking profile status:", error);
        setInviteLinkInvalid(true);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkProfileStatus();
  }, [token]);

  // Per-step validation
  const validateStep = () => {
    if (step === 1) {
      if (!firstName.trim() || !lastName.trim() || !dob || !gender || !phone.trim()) {
        setError("Please fill in all required fields");
        return false;
      }
      // Validate phone
      const phoneRegex = /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
      if (!phoneRegex.test(phone.trim())) {
        setError("Please enter a valid phone number (e.g., 123-456-7890 or (123) 456-7890)");
        return false;
      }
      if (firstName.trim().length < 2) {
        setError("First name must be at least 2 characters long");
        return false;
      }
      if (lastName.trim().length < 2) {
        setError("Last name must be at least 2 characters long");
        return false;
      }
      // Validate DOB (must be at least 18 years old and not in the future)
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 18) {
        setError("You must be at least 18 years old to register");
        return false;
      }
      if (birthDate > today) {
        setError("Date of birth cannot be in the future");
        return false;
      }
      if (!dob || isNaN(new Date(dob).getTime())) {
        setError("Please provide a valid date of birth");
        return false;
      }
    } else if (step === 2) {
      if (!address.street.trim() || !address.city.trim() || !address.state.trim() || !address.zip.trim()) {
        setError("Please fill in all required address fields");
        return false;
      }
      if (address.street.trim().length < 5) {
        setError("Street address must be at least 5 characters long");
        return false;
      }
      if (address.city.trim().length < 2) {
        setError("City name must be at least 2 characters long");
        return false;
      }
      if (address.state.trim().length < 2) {
        setError("State must be at least 2 characters long");
        return false;
      }
      const zipRegex = /^\d{5}(-\d{4})?$/;
      if (!zipRegex.test(address.zip.trim())) {
        setError("Please enter a valid ZIP code (e.g., 12345 or 12345-6789)");
        return false;
      }
    } else if (isDirector && step === 3) {
      // Basic validation for program settings for directors
      if (!programSettings.duration || !programSettings.maxPGYLevel) {
        setError("Please provide program duration and max PGY level");
        return false;
      }
    } else if ((!isDirector && step === 3) || (isDirector && step === 4)) {
      if (!password || !confirm) {
        setError("Please fill in all password fields");
        return false;
      }
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      if (!passwordRegex.test(password)) {
        setError(
          "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)"
        );
        return false;
      }
      if (password !== confirm) {
        setError("Passwords do not match");
        return false;
      }
    }
    setError("");
    return true;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep()) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    setError("");
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inviteLinkInvalid) return;
    if (!validateStep()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_ENDPOINTS.AUTH}/complete-profile/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password,
          firstName,
          middleName,
          lastName,
          dob,
          gender,
          phone,
          address,
          programDuration: isDirector ? programSettings.duration : undefined,
          programMaxPGYLevel: isDirector ? programSettings.maxPGYLevel : undefined,
          programResidentCount: isDirector ? programSettings.residentCount : undefined,
        }),
      });
      if (res.ok) {
        navigate("/login");
      } else {
        const errorData = await res.json();
        setError(errorData.message || "Error completing profile");
        setLoading(false);
      }
    } catch (error) {
      setError("Network error. Please try again.");
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center min-vh-100">
        <PageTitle title="Complete Profile" description="Complete your profile setup and account information" />
        <Card className="border-0 p-5 rounded-4 shadow text-center" style={{ maxWidth: 500, width: "100%" }}>
          <h2 className="mb-3">Checking invitation...</h2>
          <p className="text-muted mb-0">Please wait while we verify your link.</p>
        </Card>
      </Container>
    );
  }

  if (inviteLinkInvalid) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center min-vh-100">
        <PageTitle title="Page Not Found" description="The invitation link is invalid or has expired" />
        <Card className="border-0 p-5 rounded-4 shadow-sm text-center" style={{ maxWidth: 500, width: "100%" }}>
          <p className="display-1 fw-bold text-muted mb-2">404</p>
          <h5 className="mb-3 fw-bold">Page not found</h5>
          <p className="text-muted mb-4">
            This invitation link is invalid or has expired. Please use the link from your invitation email,
            or contact your program director for a new invite.
          </p>
          <Button variant="primary" onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="d-flex flex-column justify-content-center align-items-center min-vh-100">
      <PageTitle title="Complete Profile" description="Complete your profile setup and account information" />
      <div className="mb-4">
        <Stepper
          step={step}
          steps={
            isDirector
              ? [
                  { label: "Basic Info" },
                  { label: "Address" },
                  { label: "Program Setup" },
                  { label: "Set Password" },
                ]
              : [
                  { label: "Basic Info" },
                  { label: "Address" },
                  { label: "Set Password" },
                ]
          }
        />
      </div>
      <Card className="border-0 p-5 rounded-4 shadow" style={{ maxWidth: 700, width: "100%" }}>
        {profileCompleted ? (
          <div className="text-center">
            <h2 className="mb-4 text-success">Profile Already Completed</h2>
            <p className="mb-3">
              Welcome back, <strong>{userInfo?.name || 'User'}</strong>!
            </p>
            <p className="mb-4">
              Your profile has already been completed. Please log in to access your account.
            </p>
            <Button variant="primary" onClick={() => navigate("/login")}>
              Go to Login
            </Button>
          </div>
        ) : (
          <>
            <div className="d-flex flex-column justify-content-center align-items-center mb-4" style={{ maxWidth: 500, margin: "0 auto", position: 'relative' }}>
              <h2 className="text-center mb-4">Complete Your Profile</h2>
              <div className="text-hl">
              </div>
            </div>
            
            {error && <Alert variant="danger">{error}</Alert>}
      
            <Form onSubmit={(isDirector ? (step === 4) : (step === 3)) ? handleSubmit : handleNext}>
              {step === 1 && (
                <fieldset className="mb-4">
                  <legend>Basic Info</legend>
                  <Row className="g-2">
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="firstName">
                      <FloatingLabel controlId="floatingInput" label="First Name" className="mb-3 required-field"> 
                        <Form.Control 
                          type="text" 
                          value={firstName} 
                          onChange={e => setFirstName(e.target.value)} 
                          placeholder="Enter first name"
                          required 
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="middleName">
                      <FloatingLabel controlId="floatingInput" label="Middle Name" className="mb-3"> 
                        <Form.Control 
                          type="text" 
                          value={middleName} 
                          onChange={e => setMiddleName(e.target.value)} 
                          placeholder="Enter middle name (optional)"
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="lastName">
                      <FloatingLabel controlId="floatingInput" label="Last Name" className="mb-3 required-field"> 
                        <Form.Control 
                          type="text" 
                          value={lastName} 
                          onChange={e => setLastName(e.target.value)} 
                          placeholder="Enter last name"
                          required 
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="dob">
                      <FloatingLabel controlId="floatingInput" label="Date of Birth" className="mb-3 required-field"> 
                        <Form.Control 
                          type="date" 
                          value={dob} 
                          onChange={e => setDob(e.target.value)} 
                          max={new Date().toISOString().split('T')[0]}
                          required 
                        /> 
                        <Form.Text className="text-muted">Must be at least 18 years old</Form.Text>
                        </FloatingLabel>
                       
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="gender">
                      <FloatingLabel controlId="floatingInput" label="Gender" className="mb-3 required-field"> 
                        <Form.Select value={gender} onChange={e => setGender(e.target.value)} required>
                          <option value="">Select...</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </Form.Select>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="phone">
                      <FloatingLabel controlId="floatingInput" label="Phone Number" className="mb-3 required-field"> 
                        <Form.Control 
                          type="text" 
                          value={phone} 
                          onChange={e => setPhone(e.target.value)} 
                          placeholder="(123) 456-7890"
                          required 
                        />
                        <Form.Text className="text-muted">Format: (123) 456-7890 or 123-456-7890</Form.Text>
                        </FloatingLabel>
                        
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>
              )}
              {step === 2 && (
                <fieldset className="mb-4">
                  <legend>Address</legend>
                  <Row className="g-2">
                    <Col md={9} sm={12}>
                      <Form.Group controlId="street">
                      <FloatingLabel controlId="floatingInput" label="Street Address" className="mb-3 required-field"> 
                        <Form.Control 
                          type="text" 
                          value={address.street} 
                          onChange={e => setAddress({ ...address, street: e.target.value })} 
                          placeholder="123 Main Street"
                          required 
                        />
                        <Form.Text className="text-muted">Enter full street address</Form.Text>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={12}>
                      <Form.Group controlId="apt">
                      <FloatingLabel controlId="floatingInput" label="Apt / Unit" className="mb-3"> 
                        <Form.Control 
                          type="text" 
                          value={address.apt} 
                          onChange={e => setAddress({ ...address, apt: e.target.value })} 
                          placeholder="Apt 4B (optional)"
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="city">
                      <FloatingLabel controlId="floatingInput" label="City" className="mb-3 required-field"> 
                        <Form.Control 
                          type="text" 
                          value={address.city} 
                          onChange={e => setAddress({ ...address, city: e.target.value })} 
                          placeholder="New York"
                          required 
                        />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="state">
                      <FloatingLabel controlId="floatingInput" label="State" className="mb-3 required-field"> 
                        <Form.Select 
                          value={address.state} 
                          onChange={e => setAddress({ ...address, state: e.target.value })} 
                          required
                        >
                          <option value="">Select State...</option>
                          <option value="AL">Alabama</option>
                          <option value="AK">Alaska</option>
                          <option value="AZ">Arizona</option>
                          <option value="AR">Arkansas</option>
                          <option value="CA">California</option>
                          <option value="CO">Colorado</option>
                          <option value="CT">Connecticut</option>
                          <option value="DE">Delaware</option>
                          <option value="DC">District of Columbia</option>
                          <option value="FL">Florida</option>
                          <option value="GA">Georgia</option>
                          <option value="HI">Hawaii</option>
                          <option value="ID">Idaho</option>
                          <option value="IL">Illinois</option>
                          <option value="IN">Indiana</option>
                          <option value="IA">Iowa</option>
                          <option value="KS">Kansas</option>
                          <option value="KY">Kentucky</option>
                          <option value="LA">Louisiana</option>
                          <option value="ME">Maine</option>
                          <option value="MD">Maryland</option>
                          <option value="MA">Massachusetts</option>
                          <option value="MI">Michigan</option>
                          <option value="MN">Minnesota</option>
                          <option value="MS">Mississippi</option>
                          <option value="MO">Missouri</option>
                          <option value="MT">Montana</option>
                          <option value="NE">Nebraska</option>
                          <option value="NV">Nevada</option>
                          <option value="NH">New Hampshire</option>
                          <option value="NJ">New Jersey</option>
                          <option value="NM">New Mexico</option>
                          <option value="NY">New York</option>
                          <option value="NC">North Carolina</option>
                          <option value="ND">North Dakota</option>
                          <option value="OH">Ohio</option>
                          <option value="OK">Oklahoma</option>
                          <option value="OR">Oregon</option>
                          <option value="PA">Pennsylvania</option>
                          <option value="RI">Rhode Island</option>
                          <option value="SC">South Carolina</option>
                          <option value="SD">South Dakota</option>
                          <option value="TN">Tennessee</option>
                          <option value="TX">Texas</option>
                          <option value="UT">Utah</option>
                          <option value="VT">Vermont</option>
                          <option value="VA">Virginia</option>
                          <option value="WA">Washington</option>
                          <option value="WV">West Virginia</option>
                          <option value="WI">Wisconsin</option>
                          <option value="WY">Wyoming</option>
                          <option value="AS">American Samoa</option>
                          <option value="GU">Guam</option>
                          <option value="MP">Northern Mariana Islands</option>
                          <option value="PR">Puerto Rico</option>
                          <option value="VI">U.S. Virgin Islands</option>
                        </Form.Select>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="zip">
                      <FloatingLabel controlId="floatingInput" label="ZIP Code" className="mb-3 required-field"> 
                        <Form.Control 
                          type="text" 
                          value={address.zip} 
                          onChange={e => setAddress({ ...address, zip: e.target.value })} 
                          placeholder="12345"
                          required 
                        />
                        <Form.Text className="text-muted">Format: 12345 or 12345-6789</Form.Text>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>
              )}
              {isDirector && step === 3 && (
                <fieldset className="mb-4">
                  <legend>Program Setup</legend>
                  <Row className="g-2">
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="programDuration">
                        <FloatingLabel controlId="floatingProgramDuration" label="Duration (years)" className="mb-3 required-field">
                          <Form.Control
                            type="number"
                            value={programSettings.duration}
                            onChange={e =>
                              setProgramSettings({
                                ...programSettings,
                                duration: e.target.value,
                                maxPGYLevel: e.target.value || programSettings.maxPGYLevel,
                              })
                            }
                            placeholder="Enter duration in years"
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="programMaxPGYLevel">
                        <FloatingLabel controlId="floatingProgramMaxPGY" label="Max PGY Level" className="mb-3 required-field">
                          <Form.Control
                            type="number"
                            value={programSettings.maxPGYLevel}
                            onChange={e =>
                              setProgramSettings({
                                ...programSettings,
                                maxPGYLevel: e.target.value,
                              })
                            }
                            placeholder="Enter max PGY level"
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={4} sm={6} xs={12}>
                      <Form.Group controlId="programResidentCount">
                        <FloatingLabel controlId="floatingProgramResidentCount" label="Number of Residents" className="mb-3">
                          <Form.Control
                            type="number"
                            value={programSettings.residentCount}
                            onChange={e =>
                              setProgramSettings({
                                ...programSettings,
                                residentCount: e.target.value,
                              })
                            }
                            placeholder="Enter number of residents"
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>
              )}
              {(!isDirector && step === 3) || (isDirector && step === 4) ? (
                <fieldset className="mb-4">
                  <legend>Set Password</legend>
                  <Row className="g-2">
                    <Col md={6} sm={12}>
                      <Form.Group controlId="password">
                      <FloatingLabel controlId="floatingInput" label="Password" className="mb-3 required-field"> 
                        <Form.Control 
                          type="password" 
                          value={password} 
                          onChange={e => setPassword(e.target.value)} 
                          placeholder="Enter password"
                          required 
                        />
                        <Form.Text className="text-muted">
                          Must be at least 8 characters with uppercase, lowercase, number, and special character
                        </Form.Text>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col md={6} sm={12}>
                      <Form.Group controlId="confirm">
                      <FloatingLabel controlId="floatingInput" label="Confirm Password" className="mb-3 required-field"> 
                        <Form.Control 
                          type="password" 
                          value={confirm} 
                          onChange={e => setConfirm(e.target.value)} 
                          placeholder="Confirm password"
                          required 
                        />
                        <Form.Text className="text-muted">Re-enter your password</Form.Text>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>
              ) : null}
              <div className="d-flex justify-content-between mt-4">
                {step > 1 && (
                  <Button variant="secondary" onClick={handleBack} disabled={loading}>
                    Back
                  </Button>
                )}
                <div className="flex-grow-1" />
                {((!isDirector && step < 3) || (isDirector && step < 4)) && (
                  <Button variant="primary" type="submit" disabled={loading}>
                    Next
                  </Button>
                )}
                {((!isDirector && step === 3) || (isDirector && step === 4)) && (
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? "Completing..." : "Complete Profile"}
                  </Button>
                )}
              </div>
            </Form>
          </>
        )}
      </Card>
    </Container>
  );
};

export default CompleteProfile;
